import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type BuildingDataRow = Database["public"]["Tables"]["building_data"]["Row"];

export async function fetchByBuildingId(buildingId: string): Promise<BuildingDataRow[]> {
  const { data, error } = await supabase
    .from("building_data")
    .select("*")
    .eq("building_id", Number(buildingId))
    .order("attribute_type", { ascending: true })
    .order("attribute_name", { ascending: true });

  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function fetchAttribute(
  buildingId: string,
  attributeType: string,
  attributeName: string
): Promise<BuildingDataRow | null> {
  const { data, error } = await supabase
    .from("building_data")
    .select("*")
    .eq("building_id", Number(buildingId))
    .eq("attribute_type", attributeType)
    .eq("attribute_name", attributeName)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data;
}

export async function updateUserValue(
  buildingId: string,
  attributeType: string,
  attributeName: string,
  value: string | null,
  userId: number
): Promise<BuildingDataRow> {
  const { data, error } = await supabase
    .from("building_data")
    .update({
      user_value: value, // Will be null if user cleared the field (falls back to value_txt)
      reviewed: true, // User has reviewed this field by updating it
      // NOTE: We don't change source_system - it stays as the original source (doc/ehr)
      modified_by: userId,
      modified_at: new Date().toISOString(),
    })
    .eq("building_id", Number(buildingId))
    .eq("attribute_type", attributeType)
    .eq("attribute_name", attributeName)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

export async function createUserAttribute(
  buildingId: string,
  orgId: string,
  attributeType: string,
  attributeName: string,
  valueType: "text" | "number" | "date",
  value: string | null,
  userId: number
): Promise<BuildingDataRow> {
  const { data, error } = await supabase
    .from("building_data")
    .insert({
      building_id: Number(buildingId),
      org_id: orgId,
      source_type: "app",
      source_system: "user",
      attribute_type: attributeType,
      attribute_name: attributeName,
      value_type: valueType,
      value_txt: "",
      user_value: value,
      reviewed: true, // User has reviewed this field by creating it
      created_by: userId,
      modified_by: userId,
      created_at: new Date().toISOString(),
      modified_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

export async function upsertUserValue(
  buildingId: string,
  orgId: string,
  attributeType: string,
  attributeName: string,
  valueType: "text" | "number" | "date",
  value: string | null,
  userId: number
): Promise<BuildingDataRow> {
  const existing = await fetchAttribute(buildingId, attributeType, attributeName);

  if (existing) {
    // Row exists - always update (even if value is null, to clear user override)
    return updateUserValue(buildingId, attributeType, attributeName, value, userId);
  } else {
    // Row doesn't exist
    if (value === null) {
      // Don't create a row for a null value - just return a synthetic empty row
      // This shouldn't normally happen, but handles the edge case gracefully
      throw new Error("Cannot create new attribute with null value");
    }
    return createUserAttribute(
      buildingId,
      orgId,
      attributeType,
      attributeName,
      valueType,
      value,
      userId
    );
  }
}

export function getDisplayValue(row: BuildingDataRow): string | null {
  if (row.user_value !== null) return row.user_value;
  if (row.value_txt !== null && row.value_txt !== "") return row.value_txt;
  return null;
}

export function getDisplaySource(row: BuildingDataRow): "user" | "documents" | "ehitisregister" {
  if (row.user_value !== null) return "user";

  if (row.source_system === "doc") return "documents";
  if (row.source_system === "ehr") return "ehitisregister";
  if (row.source_system === "user") return "user";

  return "user";
}

/**
 * Mark a single building data attribute as reviewed by a user
 */
export async function markAsReviewed(
  buildingId: string,
  attributeType: string,
  attributeName: string,
  userId: number
): Promise<BuildingDataRow> {
  const { data, error } = await supabase
    .from("building_data")
    .update({
      reviewed: true,
      modified_by: userId,
      modified_at: new Date().toISOString(),
    })
    .eq("building_id", Number(buildingId))
    .eq("attribute_type", attributeType)
    .eq("attribute_name", attributeName)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Mark multiple building data attributes as reviewed in a single transaction
 */
export async function markMultipleAsReviewed(
  buildingId: string,
  attributes: Array<{ attributeType: string; attributeName: string }>,
  userId: number
): Promise<BuildingDataRow[]> {
  // Build an array of promises for parallel updates
  const updatePromises = attributes.map((attr) =>
    markAsReviewed(buildingId, attr.attributeType, attr.attributeName, userId)
  );

  // Execute all updates in parallel
  const results = await Promise.all(updatePromises);
  return results;
}

/**
 * Mark all unreviewed attributes for a building as reviewed
 */
export async function markAllAsReviewed(
  buildingId: string,
  userId: number
): Promise<BuildingDataRow[]> {
  const { data, error } = await supabase
    .from("building_data")
    .update({
      reviewed: true,
      modified_by: userId,
      modified_at: new Date().toISOString(),
    })
    .eq("building_id", Number(buildingId))
    .eq("reviewed", false)
    .select();

  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Get count of unreviewed attributes for a building
 */
export async function getUnreviewedCount(buildingId: string): Promise<number> {
  const { count, error } = await supabase
    .from("building_data")
    .select("*", { count: "exact", head: true })
    .eq("building_id", Number(buildingId))
    .eq("reviewed", false);

  if (error) throw new Error(error.message);
  return count ?? 0;
}
